import * as _ from 'underscore';
import { IListFetcher } from '../fetchers/IListFetcher';
import { ListResult } from '../fetchers/ListResult';
import { Filter } from '../helpers/Filter';
import { PermissionsHelper } from '../helpers/PermissionsHelper';
import { PermissionsMerger } from '../helpers/PermissionsMerger';
import { ResultHelper } from '../helpers/ResultHelper';
import { IGranteePermissions } from '../models/IGranteePermissions';
import { Module } from '../Module';
import { IGetEffectivePermissionsResult } from '../ServerApi/requests/GetEffectivePermissionsRequest';
import { GetSiteUsersRequest, IGetSiteUsersParams, IGetSiteUsersResult } from '../ServerApi/requests/GetSiteUsersRequest';
import { GetUsersRequest, IGetUsersResult } from '../ServerApi/requests/GetUsersRequest';
import { IGetItemsParams } from '../ServerApi/types/IGetItemsParams';
import { ISiteUser } from '../ServerApi/types/ISiteUser';
import { ITypedIds } from '../ServerApi/types/ITypedIds';
import { IUser, UserKeys } from '../ServerApi/types/IUser';
import { FetcherFactory } from './FetcherFactory';
import { ServerService } from './ServerService';

export class EffectivePermissionsService {

  private $q: ng.IQService;
  private server: ServerService;
  private usersFetcher: IListFetcher<IUser>;
  private siteUsersFetcher: IListFetcher<ISiteUser>;

  public static $inject = ['$q', 'ServerService', 'FetcherFactory'];

  constructor($q: ng.IQService, server: ServerService, FetcherFactory: FetcherFactory) {
    this.$q = $q;
    this.server = server;
    this.usersFetcher = FetcherFactory.getServerResourceFetcher(
      GetUsersRequest,
      (result: IGetUsersResult) => result.users);
    this.siteUsersFetcher = FetcherFactory.getServerResourceFetcher(
      GetSiteUsersRequest,
      (result: IGetSiteUsersResult) => result.users
    );
  }

  public getEffectivePermissions(
    usersParams: IGetItemsParams,
    authorizables: ITypedIds
  ): ng.IPromise<ListResult<IGranteePermissions>> {
    return this.usersFetcher.fetchList(usersParams)
      .then<ListResult<IGranteePermissions>>((users: ListResult<ISiteUser>) => {
        return this.getEffectivePermissionsInternal(users, authorizables);
      });
  }

  /**
   * Must be requested by a Site or Server admin
   */
  public getEffectivePermissionsWithSiteRole(
    siteUsersParams: IGetSiteUsersParams,
    authorizables: ITypedIds
  ): ng.IPromise<ListResult<IGranteePermissions>> {
    return this.siteUsersFetcher.fetchList(siteUsersParams)
      .then<ListResult<IGranteePermissions>>((users: ListResult<ISiteUser>) => {
        return this.getEffectivePermissionsInternal(users, authorizables);
      });
  }

  private getEffectivePermissionsInternal(users: ListResult<IUser> | ListResult<ISiteUser>, authorizables: ITypedIds) {
    if (users.items.length === 0) {
      // no users; return empty permission list
      return this.$q.when(new ListResult<IGranteePermissions>([]));
    } else {
      const userIds = _.pluck(users.items, UserKeys.id);

      const requests = PermissionsHelper.effectivePermissionsRequestsForAuthorizables(userIds, authorizables);
      const combineResultsFn = (results: IGetEffectivePermissionsResult[]) => {
        return { permissions: ResultHelper.combine(results, (result) => result.permissions) };
      };

      return this.server.sendRequestsParallel(requests, combineResultsFn)
        .then<ListResult<IGranteePermissions>>((result: IGetEffectivePermissionsResult) => {
          const list = PermissionsMerger.makeGranteePermissionsList(
            result.permissions, authorizables.ids, [], users.items);
          return new ListResult<IGranteePermissions>(list, users.index, users.total);
        });
    }
  }

  public getProjectLeaderCapability(userId: string, projectId: string): ng.IPromise<boolean> {
    const contentType = 'project';
    const userParams = { filter: Filter.and([Filter.fieldEq('id', userId)]) };
    const authorizables = { type: contentType, ids: [projectId] };

    return this.getEffectivePermissions(userParams, authorizables)
      .then((result: ListResult<IGranteePermissions>) => PermissionsHelper.hasProjectLeaderCapability(result.items[0]));
  }
}

Module.instance().service('EffectivePermissionsService', EffectivePermissionsService);
